import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { subscribePendingExpenseCount } from '../services/expenseFirestore';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../styles/adminTheme.css';
import './Dashboard.css';
import HomeManagement from '../components/HomeManagement';
import AboutManagement from '../components/AboutManagement';
import ContactManagement from '../components/ContactManagement';
import ServicesManagement from '../components/ServicesManagement';
import GalleryManagement from '../components/GalleryManagement';
import JoinUsManagement from '../components/JoinUsManagement';
import ExpenseManagement from '../components/ExpenseManagement';
import Dock from '../components/Dock';

const menuItems = [
  { id: 'home', icon: 'fas fa-house', name: 'Home' },
  { id: 'expense', icon: 'fas fa-chart-pie', name: 'Expense' },
  { id: 'contact-management', icon: 'fas fa-inbox', name: 'Inbox' },
  { id: 'about', icon: 'fas fa-info-circle', name: 'About' },
  { id: 'services', icon: 'fas fa-cogs', name: 'Services' },
  { id: 'gallery', icon: 'fas fa-images', name: 'Gallery' },
  { id: 'join-us', icon: 'fas fa-briefcase', name: 'Join Us' },
];

const pageDescriptions = {
  home: 'Update hero videos, featured reels, and welcome messaging.',
  expense: 'Manage expenses, projects, clients, and GST invoices in one place.',
  'contact-management': 'Review enquiries in a Gmail-style inbox and respond fast.',
  about: 'Maintain leadership bios, core values, and company history.',
  services: 'Curate offerings, pricing notes, and feature highlights.',
  gallery: 'Upload portfolio work and control which reels are visible.',
  'join-us': 'Manage open roles, review submissions, and close the loop with talent.',
};

const MOBILE_BREAKPOINT = 1024;

const Dashboard = () => {
  const [activePage, setActivePage] = useState('home');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [pendingExpenseCount, setPendingExpenseCount] = useState(0);
  const { currentUser, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = subscribePendingExpenseCount(setPendingExpenseCount);
    return unsub;
  }, []);

  const closeSidebarIfMobile = useCallback(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      setSidebarOpen(false);
    }
  }, []);

  const navigateToPage = useCallback((pageId) => {
    setActivePage(pageId);
    closeSidebarIfMobile();
  }, [closeSidebarIfMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((open) => !open);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully!');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  const renderActivePanel = () => {
    switch (activePage) {
      case 'home':
        return <HomeManagement />;
      case 'expense':
        return <ExpenseManagement />;
      case 'contact-management':
        return <ContactManagement />;
      case 'about':
        return <AboutManagement />;
      case 'services':
        return <ServicesManagement />;
      case 'gallery':
        return <GalleryManagement />;
      case 'join-us':
        return <JoinUsManagement />;
      default:
        return <HomeManagement />;
    }
  };

  const activeItem = menuItems.find((item) => item.id === activePage) || menuItems[0];

  return (
    <>
      <div className="dashboard-shell">
        <button type="button" className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle navigation">
          <i className={`fas ${isSidebarOpen ? 'fa-times' : 'fa-bars'}`} />
        </button>

        <div
          className={`dashboard-sidebar-backdrop ${isSidebarOpen ? 'is-visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />

        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
          <div className="sidebar-brand">
            <div>
              <p className="sidebar-eyebrow">TVC Admin</p>
              <h2>Control Center</h2>
            </div>
            <p className="sidebar-user">{currentUser?.email}</p>
          </div>
          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`sidebar-nav__item ${activePage === item.id ? 'is-active' : ''}`}
                onClick={() => navigateToPage(item.id)}
              >
                <i className={item.icon} />
                <span>{item.name}</span>
                {item.id === 'expense' && pendingExpenseCount > 0 && (
                  <span className="sidebar-nav__badge" aria-label={`${pendingExpenseCount} pending expenses`}>
                    {pendingExpenseCount}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="sidebar-help">
            <p>Need to reply quickly?</p>
            <button
              type="button"
              className="sidebar-help__btn"
              onClick={() => navigateToPage('contact-management')}
            >
              <i className="fas fa-inbox" /> Open inbox
            </button>
          </div>
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt" /> Log out
          </button>
        </aside>

        <main className="dashboard-main">
          <header className="dashboard-headline">
            <div>
              <p className="dashboard-eyebrow">Management Console</p>
              <h1>{activeItem.name} workspace</h1>
              <p className="dashboard-description">{pageDescriptions[activePage]}</p>
            </div>
            <div className="dashboard-headline__actions">
              <label className="admin-theme-switch" aria-label="Toggle dark mode">
                <input type="checkbox" checked={darkMode} onChange={toggleTheme} />
                <span className="admin-theme-switch-slider" />
              </label>
              <button type="button" className="ghost-action" onClick={() => navigate('/')}>
                <i className="fas fa-external-link-alt" /> View site
              </button>
            </div>
          </header>
          <section className="dashboard-panel-wrap">
            {renderActivePanel()}
          </section>
        </main>
      </div>
      <Dock />
    </>
  );
};

export default Dashboard;
